import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories } from '@/mocks/movies';
import { useState } from 'react';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('idle');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'website_alt') {
          params.append(key, String(value));
        }
      });

      const res = await fetch('https://readdy.ai/api/form/d9nfs3e8mbnljin59vv0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await res.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (res.ok && parsed && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        const serverMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormError(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormError('网络异常，请检查网络后重试');
    }
  };
  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '关于久久影视 - 品牌介绍 | 久久影视',
          description: '久久影视是一家专注于提供高清免费影视在线观看的平台，聚合海量免费高清电影、高清电视剧资源，画质清晰不缓冲，零成本畅享高清视听盛宴。',
          url: 'https://www.jiujiutv.com/about',
          isPartOf: { '@type': 'WebSite', name: '久久影视', url: 'https://www.jiujiutv.com' },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.jiujiutv.com/' },
            { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://www.jiujiutv.com/about' },
          ],
        }}
      />

      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20dark%20cinema%20lobby%20with%20warm%20golden%20lighting%2C%20luxurious%20theater%20interior%2C%20velvet%20curtains%2C%20premium%20atmosphere%2C%20rich%20dark%20and%20amber%20tones%2C%20sophisticated%20and%20inviting%20space%2C%20professional%20architecture%20photography&width=1920&height=600&seq=about-hero&orientation=landscape"
            alt="关于久久影视"
            title="久久影视 - 品牌介绍"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-background-50" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-4">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-50 mb-3">
                关于<strong className="text-primary-500">久久影视</strong>
              </h1>
              <p className="text-sm md:text-base text-foreground-400 max-w-xl mx-auto leading-relaxed">
                高清影视在线免费看，让每个人都能享受极致的观影体验
              </p>
            </div>
          </div>
        </section>

        <section className="py-6 md:py-10">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />

              {/* Brand Introduction */}
              <div className="bg-background-100 rounded-lg p-6 md:p-10 mb-6">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-50 mb-4">
                  品牌介绍
                </h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-400 leading-relaxed">
                  <p>
                    <strong className="text-primary-500 font-semibold">久久影视</strong>是一家专注于提供高清免费影视在线观看的平台。我们致力于为全球华语用户打造最优质的在线观影体验，聚合海量免费高清电影、高清电视剧资源，让每一位用户都能零成本畅享高清视听盛宴。
                  </p>
                  <p>
                    久久影视成立于2023年，总部位于中国。我们拥有一支由资深影视爱好者和技术专家组成的团队，深耕影视内容领域，为用户精选最优质的影视资源。无论是热门大片、经典老片、热播剧集还是高分动漫，久久影视都能满足您的观影需求。
                  </p>
                  <p>
                    我们深知用户在观看在线影视时的痛点——画质模糊、频繁缓冲、广告骚扰。因此，久久影视采用了先进的视频编码技术和CDN加速方案，确保每一部影视作品都能以高清画质流畅播放，让您告别缓冲等待，享受纯粹的观影乐趣。
                  </p>
                  <p>
                    久久影视涵盖了<strong className="text-foreground-300">电影、电视剧、动漫、综艺</strong>等多个分类，资源丰富，每日更新。无论您喜欢动作大片、悬疑烧脑、甜蜜爱情还是搞笑综艺，都能在这里找到心仪的内容。
                  </p>
                </div>
              </div>

              {/* Reputation */}
              <div className="bg-background-100 rounded-lg p-6 md:p-10 mb-6">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-50 mb-4">
                  用户口碑
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  {[
                    { value: '500万+', label: '月活跃用户' },
                    { value: '10万+', label: '影视资源' },
                    { value: '98%', label: '好评率' },
                  ].map((stat) => (
                    <div key={stat.label} className="text-center p-5 rounded-lg bg-background-200">
                      <div className="text-2xl md:text-3xl font-heading font-bold text-accent-500 mb-1">
                        {stat.value}
                      </div>
                      <div className="text-xs text-foreground-500">{stat.label}</div>
                    </div>
                  ))}
                </div>

                <div className="space-y-4">
                  {[
                    {
                      name: '王先生',
                      text: '用了久久影视快一年了，画质真的是没话说，关键是免费！之前用过好几个平台都要开会员，久久影视完全不用，强烈推荐！',
                      date: '2024-06-15',
                    },
                    {
                      name: '李女士',
                      text: '追剧党的福音！资源更新很快，热门剧集几乎同步更新。播放也很流畅，基本没遇到过缓冲的问题，体验感满分。',
                      date: '2024-05-28',
                    },
                    {
                      name: '张同学',
                      text: '作为一名大学生，久久影视真的帮了我大忙。周末窝在宿舍看电影，各种类型都有，画面清晰，推荐给所有同学了。',
                      date: '2024-04-12',
                    },
                  ].map((review) => (
                    <div key={review.name} className="p-4 rounded-lg bg-background-200">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center">
                          <span className="text-xs font-semibold text-primary-500">{review.name[0]}</span>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-foreground-50">{review.name}</div>
                          <div className="text-xs text-foreground-600">{review.date}</div>
                        </div>
                      </div>
                      <p className="text-sm text-foreground-400 leading-relaxed">
                        &ldquo;{review.text}&rdquo;
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Our Advantages */}
              <div className="bg-background-100 rounded-lg p-6 md:p-10 mb-6">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-50 mb-4">
                  我们的优势
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { icon: 'ri-shield-check-line', title: '高清画质', desc: '全站高清片源，支持超清画质播放，带来影院级观影体验' },
                    { icon: 'ri-flashlight-line', title: '极速不缓冲', desc: '全球CDN加速节点覆盖，播放流畅不卡顿，告别转圈等待' },
                    { icon: 'ri-gift-line', title: '完全免费', desc: '无需注册、无需付费、无隐藏收费，真正零成本畅享影视盛宴' },
                    { icon: 'ri-device-line', title: '多端适配', desc: '完美适配手机、平板、电脑等设备，随时随地在线观看' },
                    { icon: 'ri-refresh-line', title: '每日更新', desc: '资源库每日更新，热门影视同步上线，追剧不等待' },
                    { icon: 'ri-shield-star-line', title: '安全可靠', desc: '严格的内容筛选机制，无毒无广告骚扰，纯净观影环境' },
                  ].map((advantage) => (
                    <div key={advantage.title} className="flex gap-3 p-4 rounded-lg bg-background-200">
                      <div className="w-10 h-10 shrink-0 flex items-center justify-center rounded-full bg-primary-500/10">
                        <i className={`${advantage.icon} text-primary-500 text-lg`}></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-heading font-semibold text-foreground-50 mb-1">{advantage.title}</h3>
                        <p className="text-xs text-foreground-500 leading-relaxed">{advantage.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Content Categories */}
              <div className="bg-background-100 rounded-lg p-6 md:p-10 mb-6">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-50 mb-4">
                  内容分类
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {categories.filter(c => c.slug !== 'all').map((cat) => (
                    <div key={cat.slug} className="text-center p-4 rounded-lg bg-background-200">
                      <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-accent-500/10">
                        <i className={`${cat.icon} text-accent-500 text-lg`}></i>
                      </div>
                      <div className="text-sm font-heading font-semibold text-foreground-50">{cat.name}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact */}
              <div className="bg-background-100 rounded-lg p-6 md:p-10" id="contact">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-50 mb-4">
                  联系我们
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 shrink-0 flex items-center justify-center rounded-full bg-primary-500/10">
                        <i className="ri-mail-line text-primary-500 text-lg"></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-50 mb-0.5">电子邮箱</div>
                        <div className="text-sm text-foreground-500">contact@jiujiutv.com</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 shrink-0 flex items-center justify-center rounded-full bg-primary-500/10">
                        <i className="ri-phone-line text-primary-500 text-lg"></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-50 mb-0.5">客服电话</div>
                        <div className="text-sm text-foreground-500">400-888-9999</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 shrink-0 flex items-center justify-center rounded-full bg-primary-500/10">
                        <i className="ri-time-line text-primary-500 text-lg"></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-50 mb-0.5">工作时间</div>
                        <div className="text-sm text-foreground-500">周一至周日 9:00 - 21:00</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 shrink-0 flex items-center justify-center rounded-full bg-primary-500/10">
                        <i className="ri-map-pin-line text-primary-500 text-lg"></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-50 mb-0.5">公司地址</div>
                        <div className="text-sm text-foreground-500">北京市朝阳区望京科技园久久影视大厦</div>
                      </div>
                    </div>
                  </div>

                  <form
                    id="about-contact-form"
                    data-readdy-form="true"
                    onSubmit={handleFormSubmit}
                    className="space-y-3"
                  >
                    <div>
                      <input
                        type="text"
                        name="name"
                        placeholder="您的姓名"
                        className="w-full px-4 py-2.5 rounded-lg bg-background-200 text-sm text-foreground-50 placeholder-foreground-500 border border-background-300 focus:outline-none focus:border-primary-500/50 transition-colors duration-200"
                        required
                      />
                    </div>
                    <div>
                      <input
                        type="email"
                        name="email"
                        placeholder="您的邮箱"
                        className="w-full px-4 py-2.5 rounded-lg bg-background-200 text-sm text-foreground-50 placeholder-foreground-500 border border-background-300 focus:outline-none focus:border-primary-500/50 transition-colors duration-200"
                        required
                      />
                    </div>
                    <div>
                      <textarea
                        name="message"
                        placeholder="请输入您的留言..."
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-2.5 rounded-lg bg-background-200 text-sm text-foreground-50 placeholder-foreground-500 border border-background-300 focus:outline-none focus:border-primary-500/50 transition-colors duration-200 resize-none"
                        required
                      />
                    </div>
                    <input
                      type="text"
                      name="website_alt"
                      className="form-website-alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    <button
                      type="submit"
                      className="w-full px-6 py-2.5 rounded-lg bg-primary-500 text-background-950 text-sm font-semibold cursor-pointer hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
                    >
                      发送留言
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-xs text-green-400 text-center mt-2">留言已发送成功，我们会尽快回复您！</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-xs text-accent-500 text-center mt-2">{formError}</p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}